/**
 * « Votre première image » : one button between a newly paired machine and an
 * image, instead of a Library of twenty names to choose from.
 *
 * The server picks the model for *this* machine (app/features/system/
 * first_image.py: its graphics memory, its free disk, no account needed) and
 * answers null once any image model is ready there, so the card leaves by
 * itself after the first success. Its one button selects that model,
 * downloads it with the bar moving, fills a prompt and generates -- the same
 * path as pressing Generate, never a second one.
 */
import { APIClient } from "../../api/client.js";
import { h, setChildren } from "../../components/dom.js";
import { serverGenerates } from "../../services/execution.js";
import { downloadOnMachine } from "../../services/model-actions.js";
import { store } from "../../state/store.js";
import type { FirstImage, ModelInfo } from "../../types/index.js";

const DISMISSED_KEY = "first-image-dismissed";

function dismissed(): boolean {
	try {
		return localStorage.getItem(DISMISSED_KEY) === "1";
	} catch {
		return false;
	}
}

/** Wait, a few seconds at most, for « Générer sur » to name a computer. */
function machineChosen(timeoutMs = 15000): Promise<boolean> {
	const ready = () => serverGenerates() || store.get().executionTarget.kind === "hosted";
	if (ready()) return Promise.resolve(true);
	return new Promise((resolve) => {
		const stop = store.subscribe(
			(s) => s.executionTarget,
			() => {
				if (!ready()) return;
				stop();
				window.clearTimeout(timer);
				resolve(true);
			}
		);
		const timer = window.setTimeout(() => {
			stop();
			resolve(false);
		}, timeoutMs);
	});
}

export interface FirstImageCard {
	element: HTMLElement;
	refresh(): Promise<void>;
}

/** `generate` runs the page's own Generate with this model and prompt. */
export function createFirstImageCard(
	generate: (model: ModelInfo, prompt: string) => Promise<void>,
	/** Told when the card appears or goes, so the full form can step aside. */
	onVisibility?: (shown: boolean) => void
): FirstImageCard {
	const element = h("section", { class: "first-image-card", hidden: true });
	let offer: FirstImage | null = null;
	let running = false;

	function render(): void {
		const wasHidden = element.hidden;
		element.hidden = offer === null;
		if (wasHidden !== element.hidden) onVisibility?.(!element.hidden);
		if (!offer) return;
		const next = offer.readiness.next;
		// Only a download stands between this machine and the image: the button
		// does it. Anything else (no room on the disk, an old program) is said in
		// the Library's own words, with no button that would only fail.
		const canGo = !next || next.action === "download";
		// Readiness's own words when part of it is already there (« Terminez le
		// téléchargement »); otherwise the size, once.
		const size =
			next?.action !== "download"
				? ""
				: next.title.startsWith("Terminez")
					? " Une partie est déjà sur votre ordinateur : il reste à terminer son téléchargement."
					: offer.size_gb
						? ` Il se télécharge une seule fois (${offer.size_gb.toLocaleString("fr-FR")} Go).`
						: "";
		const status = h("p", { class: "first-image-status", "aria-live": "polite" });
		const go = h(
			"button",
			{
				class: "btn btn-primary",
				type: "button",
				onclick: () => void start(go, status),
			},
			"Préparer ma première image"
		);
		setChildren(
			element,
			h("h3", null, "Votre première image"),
			h(
				"p",
				null,
				`Pour votre ordinateur, nous avons choisi « ${offer.name} » : ${offer.reason}.${size}`
			),
			canGo
				? go
				: h(
						"p",
						{ class: "first-image-blocked" },
						`${next?.title ?? ""}${next?.detail ? ` — ${next.detail}` : ""}`
					),
			status,
			h(
				"button",
				{
					class: "btn btn-small first-image-skip",
					type: "button",
					onclick: () => {
						try {
							localStorage.setItem(DISMISSED_KEY, "1");
						} catch {
							// Private window: the card simply comes back next visit.
						}
						offer = null;
						render();
					},
				},
				"Je préfère choisir moi-même"
			)
		);
	}

	async function start(button: HTMLElement, status: HTMLElement): Promise<void> {
		if (!offer || running) return;
		running = true;
		button.setAttribute("disabled", "");
		const chosen = offer;
		try {
			const model = (await APIClient.getModels()).find((m) => m.id === chosen.model_id);
			if (!model) {
				status.textContent =
					"Ce modèle n'est plus proposé : choisissez-en un dans la Bibliothèque.";
				return;
			}
			store.setState({ selectedModel: model });
			if (chosen.readiness.next?.action === "download") {
				const failed = await downloadOnMachine(model.id, (text) => {
					status.textContent = text;
				});
				if (failed) {
					status.textContent = failed;
					return;
				}
			}
			// The server chose this model for the paired computer, but « Générer
			// sur » only points at it once the page has listed the computers
			// (the Account workspace's first refresh). Pressed before that, the
			// image went to « Cet ordinateur », which a hosted site refuses.
			status.textContent = "Recherche de votre ordinateur…";
			if (!(await machineChosen())) {
				status.textContent =
					"Votre ordinateur ne répond pas encore : vérifiez que le programme est ouvert, puis réessayez.";
				return;
			}
			status.textContent = "Votre ordinateur dessine l'image…";
			const before = store.get().currentResult;
			await generate(model, chosen.prompt);
			// The page's Generate reports its own failure (beside the result)
			// rather than throwing; its result says which way it went. Read
			// here, not asked of the server: the machine's next heartbeat, which
			// would say the model is now whole, can be seconds away -- the
			// first run of this left the card up after the image had arrived.
			// No new result at all means nothing was sent (a refusal said in a
			// toast): that is not a success either.
			const result = store.get().currentResult;
			if (result === before || result?.error) {
				status.textContent = result?.error
					? "L'image n'a pas abouti : la raison est affichée à côté du résultat."
					: "L'image n'est pas partie : réessayez dans un instant.";
				return;
			}
			offer = null;
			render();
			return;
		} catch (error) {
			status.textContent =
				error instanceof Error ? error.message : "La préparation n'a pas abouti.";
		} finally {
			running = false;
			button.removeAttribute("disabled");
		}
	}

	async function refresh(): Promise<void> {
		if (running) return;
		if (serverGenerates() || dismissed()) {
			offer = null;
		} else {
			try {
				offer = await APIClient.getFirstImage();
			} catch {
				offer = null;
			}
		}
		render();
	}

	// A machine coming online or receiving a model changes the answer.
	store.subscribe(
		(s) =>
			s.workers
				.filter((worker) => worker.online)
				.map(
					(worker) =>
						`${worker.worker_id}:${Object.keys(worker.cached_models_gb ?? {})
							.sort()
							.join(",")}:${Object.keys(worker.incomplete_models ?? {})
							.sort()
							.join(",")}`
				)
				.join("|"),
		() => void refresh()
	);

	return { element, refresh };
}
